import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Trash2, PlusCircle } from 'lucide-react';
import { biz } from '../../lib/api';

export default function BusinessSettings() {
  const [tab, setTab] = useState('templates');
  return (
    <div className="grid gap-3" data-testid="biz-settings-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-black text-[#0D47A1]">Settings</div>
        <h1 className="font-heading font-black text-2xl md:text-3xl">Business Suite Settings</h1>
      </div>
      <div className="flex gap-1 flex-wrap">
        {['templates', 'activity-types', 'sla', 'users', 'audit'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            data-testid={`biz-settings-tab-${t}`}
            className={`pill ${tab === t ? '!bg-[#E67E22] !text-white !border-[#E67E22]' : '!bg-white'} text-xs`}>
            {t}
          </button>
        ))}
      </div>
      {tab === 'templates' && <Templates/>}
      {tab === 'activity-types' && <ActivityTypes/>}
      {tab === 'sla' && <SlaRules/>}
      {tab === 'users' && <BizUsers/>}
      {tab === 'audit' && <AuditLog/>}
    </div>
  );
}

function Templates() {
  const [rows, setRows] = useState([]);
  const load = async () => setRows(await biz.listTemplates());
  useEffect(() => { load(); }, []);
  const save = async (t) => {
    try { await biz.upsertTemplate(t.key, { key: t.key, title: t.title, body: t.body }); toast.success('Saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="grid gap-2" data-testid="biz-templates">
      {rows.map((t, i) => (
        <div key={t.key} className="card-soft p-3" data-testid={`biz-tpl-${t.key}`}>
          <div className="text-xs uppercase tracking-widest text-ink/60">{t.key}</div>
          <input value={t.title} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], title: e.target.value }; setRows(c); }}
            className="rounded-lg border border-border px-2 py-1.5 w-full mt-1 font-semibold" data-testid={`biz-tpl-title-${t.key}`}/>
          <textarea rows="3" value={t.body} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], body: e.target.value }; setRows(c); }}
            className="rounded-lg border border-border px-2 py-1.5 w-full mt-1" data-testid={`biz-tpl-body-${t.key}`}/>
          <div className="text-[10px] text-ink/50 mt-1">Placeholders: {'{customer_name}, {complaint_number}, {staff_name}'}</div>
          <button className="btn-primary mt-2" onClick={() => save(t)} data-testid={`biz-tpl-save-${t.key}`}><Save className="w-4 h-4"/>Save</button>
        </div>
      ))}
    </div>
  );
}

function ActivityTypes() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const load = async () => setRows(await biz.listActivityTypes());
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name) return;
    try { await biz.addActivityType(name); setName(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await biz.delActivityType(id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="card-soft p-3" data-testid="biz-actypes">
      <div className="flex gap-2">
        <input placeholder="New type…" value={name} onChange={(e) => setName(e.target.value)}
          className="flex-1 rounded-lg border border-border px-2 py-1.5" data-testid="biz-actype-input"/>
        <button className="btn-primary" onClick={add} data-testid="biz-actype-add"><PlusCircle className="w-4 h-4"/>Add</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {rows.map((r) => (
          <span key={r.id} className="pill !bg-white text-xs">{r.name}
            <button className="ml-1 text-[#B71C1C]" onClick={() => del(r.id)} data-testid={`biz-actype-del-${r.name}`}><Trash2 className="w-3 h-3"/></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function SlaRules() {
  const [rows, setRows] = useState([]);
  const load = async () => setRows(await biz.listSlaRules());
  useEffect(() => { load(); }, []);
  const save = async (r) => {
    try {
      await biz.upsertSlaRule({
        priority: r.priority, category: r.category || null,
        response_hours: Number(r.response_hours), resolution_hours: Number(r.resolution_hours),
      });
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="card-soft p-3" data-testid="biz-sla-rules">
      <table className="min-w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-ink/60">
          <tr><th className="text-left">Priority</th><th className="text-left">Category</th><th>Response (h)</th><th>Resolution (h)</th><th/></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className="border-t border-border/50" data-testid={`biz-sla-row-${r.priority}`}>
              <td className="py-1.5">{r.priority}</td>
              <td>{r.category || 'default'}</td>
              <td><input type="number" value={r.response_hours} className="border rounded px-2 py-1 w-24"
                onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], response_hours: e.target.value }; setRows(c); }}/></td>
              <td><input type="number" value={r.resolution_hours} className="border rounded px-2 py-1 w-24"
                onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], resolution_hours: e.target.value }; setRows(c); }}/></td>
              <td><button className="pill !bg-white text-xs" onClick={() => save(r)} data-testid={`biz-sla-save-${r.priority}`}><Save className="w-3 h-3"/>Save</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BizUsers() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    biz.listBizUsers().then(setRows).catch((e) => setError(e?.response?.data?.detail || 'Admin only'));
  }, []);
  const setRole = async (uid, role) => {
    try { await biz.setBizRole(uid, role); toast.success('Role updated'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  if (error) return <div className="text-sm text-[#B71C1C]">{error}</div>;
  return (
    <div className="card-soft p-3" data-testid="biz-users">
      {!rows.length && <div className="text-sm text-ink/50">No users have a business_role yet. Assign one below when they log in.</div>}
      {rows.map((u) => (
        <div key={u.id} className="flex items-center gap-2 border-b border-border/40 py-2 text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{u.name} <span className="text-[10px] text-ink/50">({u.phone})</span></div>
            <div className="text-[10px] uppercase tracking-widest text-ink/50">{u.business_role || '—'}</div>
          </div>
          <select value={u.business_role || 'staff'} onChange={(e) => setRole(u.id, e.target.value)}
            className="rounded-lg border border-border px-2 py-1 text-xs"
            data-testid={`biz-user-role-${u.id}`}>
            {['super_admin', 'business_owner', 'staff', 'cse', 'technician', 'accounts'].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { biz.auditLog({ limit: 100 }).then(setRows).catch(() => setRows([])); }, []);
  return (
    <div className="card-soft p-3 overflow-x-auto" data-testid="biz-audit-log">
      <table className="min-w-full text-sm">
        <thead className="bg-[#FFF8EE] text-[10px] uppercase tracking-widest text-ink/60">
          <tr>{['When', 'User', 'Action', 'Type', 'Record'].map((h) => <th key={h} className="text-left px-2 py-2">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/60" data-testid={`biz-audit-row-${r.id}`}>
              <td className="px-2 py-1.5 whitespace-nowrap text-xs">{new Date(r.at).toLocaleString()}</td>
              <td className="px-2 py-1.5">{r.user_name} <span className="text-[10px] text-ink/50">({r.business_role})</span></td>
              <td className="px-2 py-1.5"><b>{r.action}</b></td>
              <td className="px-2 py-1.5 text-xs">{r.record_type}</td>
              <td className="px-2 py-1.5 font-mono text-[10px] text-ink/60">{(r.record_id || '').slice(0, 8)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="5" className="p-4 text-ink/50">No entries yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
